// Domain · BorderTypeChip
//
// Tone- and label-coded renderer for the `transactions.border_type` enum:
//
//   - `"domestic"` → grey   · "Krajowy"
//   - `"eu"`       → blue   · "UE"
//   - `"non_eu"`   → amber  · "Poza UE"
//   - `null`       → grey   · "—"
//
// Variants mirror `DprChip` / `EntityChip`:
//   - `"chip"` (default) — Pill chrome.
//   - `"inline"`         — inline tone-colored span.

import React from "react";
import { Pill, type PillSize } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { borderTypeTone, borderTypeText, type BorderType } from "@ds/domain/BorderTypeChip.helpers";

export type { BorderType } from "@ds/domain/BorderTypeChip.helpers";

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface BorderTypeChipInput {
  /**
   * `border_type` value.
   * - `"domestic"` → grey · "Krajowy"
   * - `"eu"`       → blue · "UE"
   * - `"non_eu"`   → amber · "Poza UE"
   * - `null`       → grey · "—"
   */
  value: BorderType | null | undefined;
  /** Rendering variant. Default `"chip"`. */
  variant?: "chip" | "inline";
  /** Pill size — only meaningful when `variant="chip"`. Default `"md"`. */
  size?: PillSize;
  /** `data-testid` on the root. Defaults to `"border-type-chip"`. */
  testId?: string;
}

export type BorderTypeChipProps = BorderTypeChipInput;

// ─── Component ────────────────────────────────────────────────────────────

export function BorderTypeChip({
  value,
  variant = "chip",
  size = "md",
  testId = "border-type-chip",
}: BorderTypeChipProps) {
  const tone = borderTypeTone(value);
  const text = borderTypeText(value);

  if (variant === "inline") {
    return (
      <TextValue tone={tone} testId={testId}>
        {text}
      </TextValue>
    );
  }

  return (
    <Pill tone={tone} size={size} testId={testId}>
      {text}
    </Pill>
  );
}
